import { DonateParagraph, DonateParagraphContent } from '@/__generated__/graphql';
import Link from 'next/link';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Float } from 'schema-dts';
import { StyledBlock } from './Donate.styles';
import PadlockSVG from './assets/padlock.svg';

export default function Donate({ content, referrer }: { content: DonateParagraphContent | null, referrer: string }) {
  if (content) {
    return (<DonateComponent content={content} referrer={referrer} />)
  }
  return <></>
}

function DonateComponent({content, referrer }: { content: Exclude<DonateParagraph['content'], undefined | null>, referrer: string }): ReactElement<any> {
  const { title, description, callToAction } = content;
  const isMultiple = content.__typename === 'DonateMultipleAmountsParagraph';
  const isFixed = content.__typename === 'DonateFixedAmountParagraph';
  const hasMoreFrequencies = isMultiple && (content.monthlyOptions || content.singleOptions);

  const [amount, setAmount] = useState<Float>(0);
  const [frequency, setFrequency] = useState<string>('');

  // Other input.
  const [showOther, setShowOther] = useState<boolean>(false);
  const [otherInput, setOtherInput] = useState<string>('');
  const [otherInputValid, setOtherInputValid] = useState<boolean>(true);
  const otherInputRef = useRef<HTMLInputElement | null>(null);

  const handleFrequencyClick = (value: string) => {
    if (hasMoreFrequencies) {
      // Set frequency to this value.
      setFrequency(value);

      // Set amount to default in this frequency.
      if (value === 'monthly') {
        const monthlyDefault = content.monthlyOptions?.options.find(item => item.isDefault);
        if (monthlyDefault && monthlyDefault.value) {
          setAmount(monthlyDefault.value);
        }
      }
      else if (value === 'single') {
        const singleDefault = content.singleOptions?.options.find(item => item.isDefault);
        if (singleDefault && singleDefault.value) {
          setAmount(singleDefault.value);
        }
      }
      // Hide other.
      setShowOther(false);
      // Remove other validation.
      setOtherInputValid(true);
    }
  }

  const handleAmountClick = (value: Float) => {
    // Set amount this amount.
    setAmount(value);
    // Hide other.
    setShowOther(false);
    // Remove other validation.
    setOtherInputValid(true);
  }

  const handleOtherClick = () => {
    // Set amount this amount.
    setShowOther(true);
    // Remove default from others.
    setAmount(0);
    // We've set the amount to 0, which is an invalid amount: set the error message to prevent submitting the form with
    // no values.
    setOtherInputValid(false);
  }

  const handleOtherInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Find the number of decimal points.
    const split = value.length > 0 ? value.split(".") : null;
    const decimals = (split && split[1]) ? split[1].length : 0;

    // And turn the input string into a number.
    const number = parseFloat(value);

    // Set input value.
    setOtherInput(value);

    // The input only accepts numbers between 3 > 99999, and any more than 2 decimals is not a valid currently amount.
    if (!isNaN(number) && number >= 3 && number <= 99999 && decimals <= 2) {
      // Hide validation.
      setOtherInputValid(true);

      // Set amount this amount.
      setAmount(number);

    } else {
      setOtherInputValid(false);
    }
  }

  const handleCtaClick = () => {
    // Check a condition before focusing on the input field
    if (otherInputRef && !otherInputValid && otherInputRef.current) {
      // Focus on the input field
      otherInputRef.current.focus();
    }
  };

  useEffect(() => {
    // Set type dependent variables.
    if (isFixed) {
      setAmount(content.amount);
      setFrequency(content.frequency);
    } else if (isMultiple) {
      if (content.singleOptions?.isDefault) {
        setFrequency('single');
        const singleDefault = content.singleOptions.options.find(item => item.isDefault);
        if (singleDefault && singleDefault.value) {
          setAmount(singleDefault.value);
        }
      } else if (content.monthlyOptions) {
        setFrequency('monthly');
        const monthlyDefault = content.monthlyOptions.options.find(item => item.isDefault);
        if (monthlyDefault && monthlyDefault.value) {
          setAmount(monthlyDefault.value);
        }
      }
    }
  }, [content, isFixed, isMultiple])

  const currentDescription = useMemo(() => {
    if (hasMoreFrequencies && !showOther) {
      if (frequency === 'monthly') {
        const activeOption = content.monthlyOptions?.options.find(i => i.value == amount);
        if (activeOption) {
          return activeOption.description ?? description;
        }

        const monthlyDefault = content.monthlyOptions?.options.find(item => item.isDefault);
        return monthlyDefault?.description ?? description;
      }
      else if (frequency === 'single') {
        const activeOption = content.singleOptions?.options.find(i => i.value == amount);
        if (activeOption) {
          return activeOption.description ?? description;
        }

        const singleDefault = content.singleOptions?.options.find(item => item.isDefault);
        return singleDefault?.description ?? description;
      }
    }
    return description;
  }, [amount, hasMoreFrequencies, showOther, description, frequency, content]);

  // Prepare donate URL.
  let donateUrl = new URL(callToAction.url);
  donateUrl.searchParams.append('frequency', frequency);
  donateUrl.searchParams.append('custom-amount', `${amount}`);
  donateUrl.searchParams.append('referrer', `${referrer}`);

  return <StyledBlock className='donate-component'>
      <div className="title">{title}</div>
      { isMultiple ?
      <>
        <div className={`frequency-select-wrapper ${ hasMoreFrequencies ?'two-options' : ''}`}>
          <div className="buttons-wrapper">
            {content.monthlyOptions ?
              <button onClick={() => handleFrequencyClick('monthly')} className={`${frequency === 'monthly' ? ' active' : ''}`}>
                <span>GIVE MONTHLY</span>
              </button>
            : null}
            {content.singleOptions ?
              <button onClick={() => handleFrequencyClick('single')} className={`${frequency === 'single' ? ' active' : ''}`}>
                <span>SINGLE GIFT</span>
              </button>
              : null
            }
          </div>
        </div>
        {/* Frequency selector */}
        <div className="value-select-wrapper">
        {frequency === 'monthly' && content.monthlyOptions ?
          <div className="buttons-wrapper">
            {content.monthlyOptions.options.map(option =>
              <button onClick={() => handleAmountClick(option.value)} key={`monthly-${option.value}`} className={`monthly-options${option.value === amount ? ' active' : ''}`}>
                <span>{option.label}</span>
              </button>
            )}
            {content.monthlyOptions.showFieldOther ?
              <button className={`monthly-options${showOther ? ' active' : ''}`} onClick={handleOtherClick}>
                <span>Other</span>
              </button>
              : null
            }
          </div>
          : null
        }
        {/* Options list */}
        {frequency === 'single' && content.singleOptions ?
          <div className="buttons-wrapper">
            {content.singleOptions.options.map(option =>
              <button onClick={() => handleAmountClick(option.value)} key={`single-${option.value}`} className={`single-options${option.value === amount ? ' active' : ''}`}>
                <span>{option.label}</span>
              </button>
            )}
            {content.singleOptions.showFieldOther ?
              <button className={`monthly-options${showOther ? ' active' : ''}`} onClick={handleOtherClick}>
                <span>Other</span>
              </button>
              : null
            }
          </div>
          : null
        }
        </div>
      </>
      : null }


        {/* Other field */}
        { showOther ?
          <>
            <label htmlFor="other_amount">
              <span className="visually-hidden">Enter donation amount (required)</span>
              <input
                type="number"
                id="other_amount"
                className={`other-amount${!otherInputValid ? ' errors' : ''}`}
                name="other_amount"
                step={.01}
                min={3}
                max={99999}
                placeholder="Enter an amount"
                value={otherInput}
                ref={otherInputRef}
                onChange={handleOtherInput}
                />
            </label>

            { !otherInputValid ?
              <div className="other-validation-message-wrapper" role="alert">
                <div className="other-validation-message">
                  Please enter a number between 3 and 99999
                </div>
              </div>
              : null
            }
          </>
          : null
        }

      {/* Description and CTA */}
      <div className="description">{ currentDescription ?? description }</div>
      <div className="cta-wrapper">
        <Link href={otherInputValid ? donateUrl.toString() : "#"}
            className={`${!otherInputValid ? 'errors' : ''}`}

            onClick={handleCtaClick}
            target={callToAction.target || '_self'}
        >
            {callToAction.title}<span className="visually-hidden">. Give a {frequency} £{amount} donation</span>
            <PadlockSVG />
        </Link>
      </div>
    </StyledBlock>;
}
